import tagifyCss from '@yaireo/tagify/dist/tagify.css';
import React, {useEffect, useMemo, useState} from 'react';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {useCss} from '../../../libs/shared/ui/src/css/cssContext';
import {useUnmountCancelToken} from '../../../srcCommon/helpers/hookHelpers';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {ApiRule, RuleModelStrict} from '../../models/api/ruleModel';
import {SettingsCustomFieldsPagesEnum, SettingsResourcePagesEnum} from '../../models/url/settingsUrlModel';
import {fetchApiAsync} from '../../services/http/http';
import {InterfaceEventsEnum} from '../../services/tracking';
import {getCompany, getHasRuleExpressionsFeature} from '../../store/data/company/companySelectors';
import {getHasRuleEditorHighlightErrors} from '../../store/featureFlags/featureFlagsSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsBanner} from '../banners/settingsBanner';
import {SettingsBannersEnum} from '../banners/settingsBannersContext';
import {useFetchDataFieldsTable} from '../fetchers/dataFieldsTables';
import {useRuleLibraryRecipes} from './library/recipes';
import {
  maybeAddSelectedInboxToRecipe,
  useResolveRecipePlaceholders,
} from './library/recipes/placeholderHelpers';
import {useFetchAllServices} from './ruleEditor/hooks/useFetchAllServices';
import {RuleEditor} from './ruleEditor/ruleEditor';
import {RuleEditorSkeleton} from './ruleEditor/ruleEditorSkeleton';
import {parseApiError, trackRuleEvent} from './ruleHelpers';
import {RuleIntegration} from './ruleIntegrationModel';
import {isComplexRecipeId} from './ruleRecipeModel';
import {SettingsRulesComplexRecipesEditor} from './settingsRulesComplexRecipesEditor';

/*
 * Types.
 */

interface RuleEditorWrapperProps {
  panelId: SettingsPanelId;
  resourcePage: SettingsResourcePagesEnum;
  namespace: string;
  ruleId?: number;
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  ruleIntegration: RuleIntegration;
  recipeId?: string;
  bundleId?: number;
  selectedInboxId?: number;
}

/*
 * Component.
 */

export const RuleEditorWrapper = (props: RuleEditorWrapperProps) => {
  const {
    panelId,
    resourcePage,
    namespace,
    ruleIntegration,
    ruleId,
    breadcrumbs,
    recipeId,
    bundleId,
    selectedInboxId,
  } = props;

  // Enable tagify styles across the rule editor.
  useCss(tagifyCss);

  const isRecipe = recipeId !== undefined;
  const company = useAppSelector(getCompany);
  const [rule, setRule] = useState<RuleModelStrict | null>(null);
  const [errorBannerMessage, setErrorBannerMessage] = useState(null);
  const hasRuleExpressions = useAppSelector(getHasRuleExpressionsFeature);
  const cancelToken = useUnmountCancelToken();
  const isEditingRule = resourcePage === SettingsResourcePagesEnum.EDIT;
  const maybeRecipe = useRuleLibraryRecipes(ruleIntegration).find((recipe) => recipe.id === recipeId);
  const hasHighlightErrorsFlag = useAppSelector(getHasRuleEditorHighlightErrors);

  const dataTableId = rule?.bundle?.dataFieldsTableId;
  const {dataTable, isLoading: isDataTableLoading} = useFetchDataFieldsTable(dataTableId, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    error: servicesError,
    status,
    teammateCustomFields,
    inboxCustomFields,
    accountCustomFields,
    contactCustomFields,
  } = useFetchAllServices(namespace);
  const customFields = useMemo(
    () => ({
      [SettingsCustomFieldsPagesEnum.CARDS]: contactCustomFields,
      [SettingsCustomFieldsPagesEnum.TEAMMATES]: teammateCustomFields || [],
      [SettingsCustomFieldsPagesEnum.INBOXES]: inboxCustomFields || [],
      [SettingsCustomFieldsPagesEnum.ACCOUNTS]: accountCustomFields || [],
    }),
    [contactCustomFields, teammateCustomFields, inboxCustomFields, accountCustomFields],
  );
  const servicesAreLoading = status !== AsyncStatusesEnum.LOADED || isDataTableLoading;

  const recipeWithSelectedInbox = useMemo(
    () => maybeAddSelectedInboxToRecipe(maybeRecipe, selectedInboxId),
    [maybeRecipe, selectedInboxId],
  );

  const [shouldResolveRecipe, resolvedRecipe] = useResolveRecipePlaceholders(
    recipeWithSelectedInbox,
    servicesAreLoading ? undefined : customFields,
  );

  const navigateToRules = () => {
    // If this is a recipe, we push the Rule Library route in the breadcrumb (see ruleEditorBreadcrumbs.tsx).
    const ruleListBreadcrumb = breadcrumbs[breadcrumbs.length - (isRecipe ? 2 : 1)];
    if (!ruleListBreadcrumb) {
      return;
    }

    ruleListBreadcrumb.onBreadcrumbClick(undefined);
  };

  useEffect(() => {
    // If we're editing and there is a rule id, fetch the corresponding rule.
    if (isEditingRule && ruleId) {
      // Need coalessting so we have strict rule model
      fetchApiAsync(ApiRule, `${company.url}/rules/${ruleId}`, {cancelToken}).then(
        (r) => setRule(r as RuleModelStrict),
        (err) => setErrorBannerMessage(parseApiError(err)),
      );
    }
  }, [ruleId, namespace, company.url, isEditingRule, cancelToken]);

  useEffect(() => {
    if (rule) {
      trackRuleEvent(InterfaceEventsEnum.SETTINGS_RULE_VIEW, rule, namespace, maybeRecipe?.id);
    }
  }, [namespace, rule, maybeRecipe]);

  useEffect(() => {
    if (servicesError) {
      setErrorBannerMessage(parseApiError(servicesError));
    }
  }, [servicesError]);

  if (errorBannerMessage) {
    return (
      <SettingsBanner type={SettingsBannersEnum.ERROR} onDismissClick={navigateToRules}>
        {errorBannerMessage}
      </SettingsBanner>
    );
  }

  if (servicesAreLoading || (shouldResolveRecipe && !resolvedRecipe)) {
    return (
      <RuleEditorSkeleton
        breadcrumbs={breadcrumbs}
        namespace={namespace}
        isEditing={isEditingRule}
        isActive={rule?.active ?? true}
        recipe={maybeRecipe}
      />
    );
  }

  if (isRecipe && isComplexRecipeId(recipeId)) {
    return (
      <SettingsRulesComplexRecipesEditor
        breadcrumbs={breadcrumbs}
        bundleId={bundleId}
        customFields={customFields}
        ruleIntegration={ruleIntegration}
        isEditingRule={isEditingRule}
        namespace={namespace}
        recipe={recipeId}
        navigateToRules={navigateToRules}
        setErrorBannerMessage={setErrorBannerMessage}
        hasHighlightErrorsFlag={hasHighlightErrorsFlag}
      />
    );
  }

  if (isEditingRule && ruleId) {
    if (!rule) {
      return (
        <RuleEditorSkeleton
          breadcrumbs={breadcrumbs}
          namespace={namespace}
          isEditing={isEditingRule}
          isActive
          recipe={maybeRecipe}
        />
      );
    }

    return (
      <RuleEditor
        key={rule.id}
        panelId={panelId}
        rule={rule}
        namespace={namespace}
        ruleIntegration={ruleIntegration}
        hasRuleExpressions={hasRuleExpressions}
        navigateToRules={navigateToRules}
        customFields={customFields}
        breadcrumbs={breadcrumbs}
        hasHighlightErrorsFlag={hasHighlightErrorsFlag}
        initialDataTable={dataTable}
      />
    );
  }

  return (
    <RuleEditor
      panelId={panelId}
      recipe={resolvedRecipe}
      namespace={namespace}
      ruleIntegration={ruleIntegration}
      hasRuleExpressions={hasRuleExpressions}
      navigateToRules={navigateToRules}
      customFields={customFields}
      breadcrumbs={breadcrumbs}
      hasHighlightErrorsFlag={hasHighlightErrorsFlag}
      initialDataTable={undefined}
    />
  );
};
